import React, { useEffect, useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { notify } from "../../../components/Toast";
import { getSubscriptionDetails } from "../../../services/clientWebsite";
import "./SubscriptionDetails.css";
import { SubscriptionDetailsShimmer } from "../../../components/Shimmer/Shimmer";
import { useDispatch, useSelector } from "react-redux";

// Default sidebar options in case API fails
const fallbackSidebarOptions = [
  { key: "overview", label: "Overview" },
  { key: "plan", label: "Plan Info" },
  { key: "coach", label: "Coach Access" },
  { key: "ebooks", label: "eBooks Access" },
  { key: "billing", label: "Billing Info" },
];

// Default section details in case API fails
const fallbackSectionDetails = {
  overview: "Fallback overview content...",
  plan: "Fallback plan content...",
  coach: "Fallback coach content...",
  ebooks: "Fallback ebooks content...",
  billing: "Fallback billing content...",
};

const SubscriptionDetails = () => {
  // State management
  const [activeSection, setActiveSection] = useState("overview"); // current active section
  const [pdfUrl, setPdfUrl] = useState(null); // PDF file if available
  const [loading, setLoading] = useState(true); // shimmer loading
  const [sidebarOptions, setSidebarOptions] = useState(fallbackSidebarOptions); // sidebar menu
  const [sectionDetails, setSectionDetails] = useState(fallbackSectionDetails); // section content
  const dispatch = useDispatch();
  const purchasedSubscription = useSelector(
    (state) => state.auth.purchasedSubscription
  );
  const sectionRefs = useRef({}); // store refs for each section block

  // Smooth scroll to a section
  const scrollToSection = (key) => {
    const section = sectionRefs.current[key];
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Handle sidebar option click
  const handleSidebarClick = (key) => {
    setActiveSection(key);
    scrollToSection(key);
  };

  // Highlight active section while scrolling
  useEffect(() => {
    if (pdfUrl || loading) return; // skip if PDF is shown or still loading

    const observer = new IntersectionObserver(
      (entries) => {
        // Find most visible section
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length > 0) {
          const key = visible[0].target.getAttribute("data-key");
          if (key) setActiveSection(key);
        }
      },
      {
        root: document.querySelector(".details-scroll-content"),
        threshold: [0.3, 0.6, 0.9], // section must be 30-90% visible
      }
    );

    // Observe each section
    sidebarOptions.forEach((section) => {
      const el = sectionRefs.current[section.key];
      if (el) {
        el.setAttribute("data-key", section.key);
        observer.observe(el);
      }
    });

    return () => observer.disconnect(); // cleanup
  }, [pdfUrl, sidebarOptions, loading]);

  // Fetch subscription details from API
  const fetchSubscriptionDetails = async () => {
    const shimmerMinTime = 3000; // minimum shimmer time (ms)
    const startTime = Date.now();

    try {
      const response = await getSubscriptionDetails();

      if (response.success) {
        // Search through all data objects to find content file and sections
        let contentFile = null;
        let contentData = [];
        let hasDocument = null;
        let subscriptionId = null;
        let chat_enable = null;

        // Loop through all data objects to find the required information
        for (const dataItem of response.data || []) {
          // Get content file if not found yet
          if (!contentFile && dataItem?.subscription_content?.document_file) {
            contentFile = dataItem.subscription_content.document_file_url;
          }
          
          // Get content sections if not found yet
          if (contentData.length === 0 && dataItem?.subscription_content?.sections) {
            contentData = dataItem.subscription_content.sections;
          }
          
          // Get has document if not found yet
          if (hasDocument === null && dataItem?.subscription_content?.has_document !== undefined) {
            hasDocument = dataItem.subscription_content.has_document;
          }
          
          // Get subscription ID if not found yet
          if (!subscriptionId && dataItem?.subscription_details?.subscription_id) {
            subscriptionId = dataItem.subscription_details.subscription_id;
          }
          
          // Get chat enable if not found yet
          if (chat_enable === null && dataItem?.user_subscription?.chat_enable !== undefined) {
            chat_enable = dataItem.user_subscription.chat_enable;
          }
        }
        // Set PDF URL from found content file
        setPdfUrl(contentFile);

        // Process sections data
        const titleKeyMap = {};
        contentData.forEach((section, index) => {
          const key = `section_${index + 1}`;
          titleKeyMap[section.title] = key;
        });

        // Filter only valid sections
        const validSections = contentData.filter((section) => titleKeyMap[section.title]);

        // Sidebar options from API
        const sidebarOpts = validSections.map((section) => ({
          key: titleKeyMap[section.title],
          label: section.title,
        }));

        // Section details mapping
        const sectionDetailsObj = {};
        validSections.forEach((section) => {
          const key = titleKeyMap[section.title];
          sectionDetailsObj[key] = section.body;
        });

        // Update state
        setSidebarOptions(sidebarOpts);
        setSectionDetails(sectionDetailsObj);
      }
    } catch (error) {
      // notify.error("Failed to fetch subscription details"); // silent fail
    } finally {
      // Ensure shimmer shows at least 3 seconds
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, shimmerMinTime - elapsed);
      setTimeout(() => setLoading(false), remaining);
    }
  };

  // Fetch details on mount
  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  return (
    <div className="subscription-container" id="subscription-details">
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "#111111",
          padding: 2,
          borderBottom: "1px dotted #cccbcbff",
        }}
      >
        <Typography variant="overline" className="subscription-heading">
          SUBSCRIPTION DETAILS
        </Typography>
      </Box>

      {/* Main Content */}
      <div className="main-content container">
        {loading ? (
          // Show shimmer while loading
          <SubscriptionDetailsShimmer />
        ) : (
          <>
            {/* Sidebar (only if PDF is not available) */}
            {!pdfUrl && (
              <aside className="sidebar-subscription">
                <div className="sidebar-options">
                  {sidebarOptions.map((option) => (
                    <div
                      key={option.key}
                      className={`sidebar-option ${activeSection === option.key ? "active" : ""}`}
                      onClick={() => handleSidebarClick(option.key)}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              </aside>
            )}

            {/* Content Section (PDF OR text sections) */}
            {pdfUrl ? (
              <div style={{ flex: 1 }}>
                <iframe
                  src={pdfUrl}
                  title="PDF Preview"
                  style={{ width: "100%", height: "100vh", border: "none" }}
                />
              </div>
            ) : (
              <div className="details-scroll-content">
                {sidebarOptions.map(({ key, label }) => (
                  <div
                    key={key}
                    ref={(el) => (sectionRefs.current[key] = el)}
                    className="section-block"
                    data-key={key}
                  >
                    <h2>{label}</h2>
                    <div dangerouslySetInnerHTML={{ __html: sectionDetails[key] || "" }} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionDetails;
